import { useState, useEffect, useRef } from 'react';
import { CountryCode, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { validatePhoneNumber, detectUserCountry, validatePhoneWithTwilio } from '../lib/phoneValidation';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidation?: (isValid: boolean, formattedNumber?: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  useTwilioValidation?: boolean;
}

export default function PhoneInput({
  value,
  onChange,
  onValidation,
  placeholder = "Phone number",
  required = false,
  className = "",
  useTwilioValidation = false
}: PhoneInputProps) {
  const [country, setCountry] = useState<CountryCode>('US');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [initialValue, setInitialValue] = useState(value);
  const validationTimeoutRef = useRef<NodeJS.Timeout>();

  // Helper function to count digits in a string
  const countDigits = (str: string): number => {
    return str.replace(/\D/g, '').length;
  };

  // Detect user's country on component mount
  useEffect(() => {
    detectUserCountry().then(({ countryCode }) => {
      setCountry(countryCode);
    });
  }, []);

  // Debounced validation - only validate when user stops typing for 3 seconds
  useEffect(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    if (!value || value.trim() === '') {
      setValidationError('');
      setIsValid(null);
      setIsValidating(false);
      onValidation?.(false);
      return;
    }

    // Check if we have enough digits to validate
    const digitCount = countDigits(value);
    if (digitCount < 6) {
      setIsValid(null);
      setValidationError('');
      setIsValidating(false);
      onValidation?.(false);
      return;
    }

    // If the value hasn't changed from initial value and we're using Twilio validation,
    // don't trigger validation
    if (useTwilioValidation && value === initialValue) {
      setIsValid(null);
      setValidationError('');
      setIsValidating(false);
      return;
    }

    // Clear previous validation state when user is typing
    setIsValid(null);
    setValidationError('');
    setIsTyping(true);

    validationTimeoutRef.current = setTimeout(async () => {
      await validatePhone(value);
    }, 3000); // Increased to 3 seconds to avoid premature validation calls

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        setIsValidating(false);
        setIsTyping(false);
      }
    };
  }, [value, country, useTwilioValidation]);

  const validatePhone = async (phoneNumber: string) => {
    setIsValidating(true);
    setIsTyping(false);
    setValidationError('');

    try {
      let result;
      
      if (useTwilioValidation) {
        // Use Twilio for server-side validation
        result = await validatePhoneWithTwilio(phoneNumber, country);
      } else {
        // Use client-side validation
        result = validatePhoneNumber(phoneNumber, country);
      }

      setIsValid(result.isValid);
      if (result.error) {
        setValidationError(result.error);
      }

      onValidation?.(result.isValid, result.formattedNumber);
    } catch (error) {
      console.error('Phone validation error:', error);
      setValidationError('Validation failed');
      setIsValid(false);
      onValidation?.(false);
    } finally {
      setIsValidating(false);
    }
  };

  const getInputClassName = () => {
    let baseClass = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors text-sm ${className}`;
    
    if (isValid === false && value) {
      baseClass += ' border-red-300 focus:ring-red-500 focus:border-red-500';
    } else {
      baseClass += ' border-gray-300 focus:ring-blue-500 focus:border-blue-500';
    }

    return baseClass;
  };

  const getValidationIcon = () => {
    if (isValidating) {
      return (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
      );
    }

    if (isValid === false && value) {
      return (
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }

    return null;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    onChange(rawValue);
    
    // Clear validation state immediately when user starts typing
    if (isValid !== null) {
      setIsValid(null);
      setValidationError('');
    }
  };

  const handleBlur = async () => {
    // Validate immediately when user leaves the field
    if (value && value.trim() !== '') {
      // Clear any existing timeout since we're validating now
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      await validatePhone(value);
    }
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountry(e.target.value as CountryCode);
  };

  const formatPhoneWithCountryCode = (phone: string, countryCode: CountryCode) => {
    if (!phone) return '';
    const callingCode = getCountryCallingCode(countryCode);
    
    // If phone already starts with country code, return as is
    if (phone.startsWith('+')) return phone;
    
    // If phone starts with the calling code (without +), add the +
    if (phone.startsWith(callingCode)) return `+${phone}`;
    
    // Otherwise, prepend the full country code
    return `+${callingCode} ${phone}`;
  };

  const getCountryOptions = () => {
    const countries = getCountries();
    const countryNames: Record<string, string> = {
      'US': 'United States',
      'CA': 'Canada',
      'GB': 'United Kingdom',
      'AU': 'Australia',
      'DE': 'Germany',
      'FR': 'France',
      'ES': 'Spain',
      'IT': 'Italy',
      'JP': 'Japan',
      'KR': 'South Korea',
      'CN': 'China',
      'IN': 'India',
      'BR': 'Brazil',
      'MX': 'Mexico',
      'AR': 'Argentina',
      'CL': 'Chile',
      'CO': 'Colombia',
      'PE': 'Peru',
      'VE': 'Venezuela',
      'ZA': 'South Africa',
      'NG': 'Nigeria',
      'KE': 'Kenya',
      'EG': 'Egypt',
      'MA': 'Morocco',
      'GH': 'Ghana',
      'TZ': 'Tanzania',
      'UG': 'Uganda',
      'ZW': 'Zimbabwe',
      'BW': 'Botswana',
      'ZM': 'Zambia',
      'MW': 'Malawi',
      'MZ': 'Mozambique',
      'AO': 'Angola',
      'NA': 'Namibia',
      'SZ': 'Eswatini',
      'LS': 'Lesotho'
    };

    return countries.map(code => ({
      code,
      name: countryNames[code] || code,
      callingCode: getCountryCallingCode(code)
    })).sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <div className="relative">
      <div className="flex">
        <select
          value={country}
          onChange={handleCountryChange}
          className="flex-shrink-0 px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {getCountryOptions().map(({ code, name, callingCode }) => (
            <option key={code} value={code}>
              {code} +{callingCode}
            </option>
          ))}
        </select>
        
        <div className="flex-1 relative">
          <input
            type="tel"
            value={value}
            onChange={handlePhoneChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={getInputClassName().replace('rounded-md', 'rounded-r-md border-l-0')}
          />
          
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {getValidationIcon()}
          </div>
        </div>
      </div>
      
      {validationError && (
        <p className="mt-1 text-sm text-red-600">{validationError}</p>
      )}
      
      {required && !value && (
        <p className="mt-1 text-sm text-gray-500">Phone number is required</p>
      )}
    </div>
  );
}
